// src/components/operationCenter/widgets/GenericStatTableWidget.tsx
// Scrollable data table from any endpoint — configurable columns, sorting, formatting.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Settings, ArrowUpDown } from "lucide-react";
import type { WidgetProps, GenericStatTableConfig } from "@/types/dashboard";
import type { ValueFormat } from "./shared/genericWidgetUtils";
import {
  fetchEndpoint,
  extractRecords,
  parseParams,
  paramsToString,
  formatValue,
  changeColor,
} from "./shared/genericWidgetUtils";
import { WIDGET_EMPTY } from "../WidgetWrapper";

type ColDef = NonNullable<GenericStatTableConfig["columns"]>[number];
const EMPTY_COL: ColDef = { key: "", label: "", format: "number", is_change: false };
const FORMAT_OPTIONS: ValueFormat[] = ["number", "currency", "compact", "percent", "ratio"];

const GenericStatTableWidget: React.FC<WidgetProps<GenericStatTableConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as GenericStatTableConfig;

  const [showConfig, setShowConfig] = useState(!config.endpoint);
  const [draftEndpoint, setDraftEndpoint] = useState(config.endpoint ?? "");
  const [draftTitle, setDraftTitle] = useState(config.title ?? "Table");
  const [draftParams, setDraftParams] = useState(paramsToString(config.params));
  const [draftCols, setDraftCols] = useState<ColDef[]>(
    config.columns?.length ? config.columns : [{ ...EMPTY_COL }],
  );
  const [draftSortBy, setDraftSortBy] = useState(config.sort_by ?? "");
  const [draftSortDir, setDraftSortDir] = useState<"asc" | "desc">(config.sort_dir ?? "desc");
  const [draftLimit, setDraftLimit] = useState(String(config.limit ?? 50));

  // Local sort state
  const [sortKey, setSortKey] = useState(config.sort_by ?? "");
  const [sortDir, setSortDir] = useState<"asc" | "desc">(config.sort_dir ?? "desc");

  useEffect(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Table");
    setDraftParams(paramsToString(config.params));
    setDraftCols(config.columns?.length ? config.columns : [{ ...EMPTY_COL }]);
    setDraftSortBy(config.sort_by ?? "");
    setDraftSortDir(config.sort_dir ?? "desc");
    setDraftLimit(String(config.limit ?? 50));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.id]);

  const { data: rawData, isLoading, error, refetch } = useQuery({
    queryKey: ["generic_stat_table", config.endpoint, config.params],
    queryFn: ({ signal }) => fetchEndpoint(config.endpoint, config.params ?? {}, signal),
    enabled: Boolean(config.endpoint),
    staleTime: 60_000,
    retry: 1,
  });

  const rows = useMemo(() => {
    if (!rawData) return [];
    let records = extractRecords(rawData);

    const activeSort = sortKey || config.sort_by;
    if (activeSort) {
      records = [...records].sort((a, b) => {
        const av = Number(a[activeSort] ?? 0);
        const bv = Number(b[activeSort] ?? 0);
        return sortDir === "desc" ? bv - av : av - bv;
      });
    }

    return records.slice(0, config.limit ?? 50);
  }, [rawData, sortKey, sortDir, config.sort_by, config.limit]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const handleSave = useCallback(() => {
    const columns = draftCols.filter((c) => c.key.trim());
    const params = parseParams(draftParams);
    onConfigChange?.({
      endpoint: draftEndpoint,
      title: draftTitle,
      columns: columns.length ? columns : [EMPTY_COL],
      ...(draftSortBy ? { sort_by: draftSortBy } : {}),
      sort_dir: draftSortDir,
      limit: parseInt(draftLimit, 10) || 50,
      ...(Object.keys(params).length ? { params } : {}),
    });
    setShowConfig(false);
  }, [draftEndpoint, draftTitle, draftParams, draftCols, draftSortBy, draftSortDir, draftLimit, onConfigChange]);

  const handleCancel = useCallback(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Table");
    setDraftParams(paramsToString(config.params));
    setDraftCols(config.columns?.length ? config.columns : [{ ...EMPTY_COL }]);
    setDraftSortBy(config.sort_by ?? "");
    setDraftSortDir(config.sort_dir ?? "desc");
    setDraftLimit(String(config.limit ?? 50));
    setShowConfig(false);
  }, [config]);

  const updateCol = (i: number, patch: Partial<ColDef>) =>
    setDraftCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addCol = () =>
    setDraftCols((prev) => (prev.length < 8 ? [...prev, { ...EMPTY_COL }] : prev));
  const removeCol = (i: number) =>
    setDraftCols((prev) => prev.filter((_, idx) => idx !== i));

  const columns = config.columns?.filter((c) => c.key) ?? [];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium truncate">
          {config.title || "Stat Table"}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => refetch()}
            disabled={!config.endpoint}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200"
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
          <button
            onClick={() => setShowConfig((s) => !s)}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200"
            title="Configure"
          >
            <Settings size={12} />
          </button>
        </div>
      </div>

      {/* Config form */}
      {showConfig && (
        <div className="flex-shrink-0 p-3 border-b border-slate-700/30 bg-gray-900/50 space-y-2 overflow-y-auto max-h-72">
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Endpoint</label>
              <input type="text" value={draftEndpoint} onChange={(e) => setDraftEndpoint(e.target.value)}
                placeholder="/api/v1/movers" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Title</label>
              <input type="text" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Table" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="min-w-[100px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Query Params</label>
              <input type="text" value={draftParams} onChange={(e) => setDraftParams(e.target.value)}
                placeholder="limit=20" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[100px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Sort By</label>
              <input type="text" value={draftSortBy} onChange={(e) => setDraftSortBy(e.target.value)}
                placeholder="change_24h" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[60px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Limit</label>
              <input type="number" value={draftLimit} onChange={(e) => setDraftLimit(e.target.value)}
                min={1} max={500} className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
          </div>

          {/* Columns */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Columns</label>
              {draftCols.length < 8 && (
                <button onClick={addCol} className="text-[10px] text-blue-400 hover:text-blue-300">+ Add</button>
              )}
            </div>
            <div className="space-y-1">
              {draftCols.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_80px_60px_auto] gap-1 items-center">
                  <input type="text" value={c.key} onChange={(e) => updateCol(i, { key: e.target.value })}
                    placeholder="field" className="input input-xs bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs" />
                  <input type="text" value={c.label} onChange={(e) => updateCol(i, { label: e.target.value })}
                    placeholder="Label" className="input input-xs bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs" />
                  <select value={c.format ?? "number"} onChange={(e) => updateCol(i, { format: e.target.value as ValueFormat })}
                    className="select select-xs bg-gray-800/60 border-gray-700/50 text-slate-200 text-[10px]">
                    {FORMAT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!c.is_change} onChange={(e) => updateCol(i, { is_change: e.target.checked })}
                      className="checkbox checkbox-xs" />
                    <span className="text-[10px] text-slate-400">Δ</span>
                  </label>
                  {draftCols.length > 1 && (
                    <button onClick={() => removeCol(i)} className="text-rose-500 hover:text-rose-400 text-xs">×</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={handleCancel} className="btn btn-xs btn-ghost text-slate-400">Cancel</button>
            <button onClick={handleSave} className="btn btn-xs btn-primary">Apply</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {!config.endpoint ? (
          <div className={WIDGET_EMPTY}>
            <div className="text-center space-y-2">
              <p>No endpoint configured</p>
              <button onClick={() => setShowConfig(true)} className="btn btn-xs btn-outline btn-primary">Configure</button>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="loading loading-spinner loading-sm text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-rose-400 text-xs text-center px-4">
            {error instanceof Error ? error.message : "Error loading data"}
          </div>
        ) : !rows.length ? (
          <div className="flex items-center justify-center h-full text-slate-500 text-sm">
            No data
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Column headers */}
            {columns.length > 0 && (
              <div
                className="flex-shrink-0 grid gap-1 px-3 py-1.5 border-b border-slate-700/30 bg-slate-800/20"
                style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
              >
                {columns.map((col) => (
                  <button
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="flex items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-slate-200 truncate"
                  >
                    <span className="truncate">{col.label || col.key}</span>
                    {(sortKey || config.sort_by) === col.key && (
                      <ArrowUpDown size={9} className="flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Rows */}
            <div className="flex-1 overflow-y-auto">
              {rows.map((row, idx) => (
                <div
                  key={idx}
                  className={`grid gap-1 px-3 py-1.5 hover:bg-slate-700/20 transition-colors ${
                    idx % 2 === 0 ? "bg-transparent" : "bg-slate-800/10"
                  }`}
                  style={{
                    gridTemplateColumns: columns.length
                      ? `repeat(${columns.length}, minmax(0, 1fr))`
                      : "1fr",
                  }}
                >
                  {columns.length ? (
                    columns.map((col) => {
                      const val = row[col.key];
                      const numVal = typeof val === "number" ? val : parseFloat(String(val));
                      const colorClass = col.is_change ? changeColor(numVal) : "text-slate-200";
                      return (
                        <div key={col.key} className={`text-[11px] tabular-nums truncate ${colorClass}`}>
                          {col.is_change && numVal >= 0 ? "+" : ""}
                          {val != null ? formatValue(val, col.format ?? "number") : "—"}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-[11px] text-slate-300 truncate">
                      {JSON.stringify(row)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-3 py-1 border-t border-slate-700/30 bg-slate-800/20">
              <span className="text-[10px] text-slate-500">{rows.length} rows</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericStatTableWidget;
